import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Printer, X, Loader2, Download, ExternalLink, CheckCircle2 } from "lucide-react";
import LocationPicker from "./LocationPicker";
import { centroidForDepartamento } from "../lib/svCentroids";

/**
 * Modal to build a realistic C807 test label using the SAME fields a customer
 * fills at checkout: contact info, address, dept/muni, service, weight/content,
 * and the optional Plus Code + Google Maps QR (via LocationPicker).
 *
 * Uses the real C807 API — the resulting guía appears in the C807 portal but
 * with an "orden=TEST-HHMMSS" tag so it's easy to identify as a test.
 */
export default function C807SampleLabelModal({ open, onClose }) {
    const [departamentos, setDepartamentos] = useState([]);
    const [municipios, setMunicipios] = useState([]);
    const [loadingMun, setLoadingMun] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null); // { guia_numero, orden, blob_url, mock, location_appended }
    const blobUrlRef = useRef(null);
    const [form, setForm] = useState({
        nombre: "Cliente de Prueba",
        telefono: "70000000",
        correo: "test@terraboutique.local",
        direccion: "Calle de Prueba #123, Colonia Ejemplo",
        reference: "Casa color blanco, portón negro",
        departamento_id: 6,
        municipio_id: 72,
        tipo_servicio: "SER",
        monto_cce: "",
        tipo_entrega: "NRML",
        peso: 1.0,
        unidad_medida: "LB",
        contenido: "Perfume Terra Boutique (muestra)",
        location: null, // { plus_code, plus_code_human, lat, lng, maps_url, confirmed }
    });

    // Lock body scroll while modal open
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    // Release blob URL when modal is fully unmounted
    useEffect(() => {
        return () => {
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, []);

    const handleClose = () => {
        // Release blob and reset result when closing
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
        setResult(null);
        onClose?.();
    };

    // Load departamentos on open
    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await api.get("/c807-public/catalogo/departamentos");
                setDepartamentos(data.items || []);
            } catch (e) {
                toast.error("No se pudo cargar la lista de departamentos");
            }
        })();
    }, [open]);

    // Load municipios when departamento changes
    useEffect(() => {
        if (!open || !form.departamento_id) return;
        (async () => {
            setLoadingMun(true);
            try {
                const { data } = await api.get(`/c807-public/catalogo/municipios?departamento=${form.departamento_id}`);
                setMunicipios(data.items || []);
            } catch (e) {
                setMunicipios([]);
            } finally { setLoadingMun(false); }
        })();
    }, [form.departamento_id, open]);

    const depName = useMemo(
        () => (departamentos.find((d) => String(d.id) === String(form.departamento_id)) || {}).nombre || "",
        [departamentos, form.departamento_id]
    );
    const munName = useMemo(
        () => (municipios.find((m) => String(m.id) === String(form.municipio_id)) || {}).nombre || "",
        [municipios, form.municipio_id]
    );

    const submit = async () => {
        if (!form.nombre.trim()) return toast.error("Ingresa el nombre del destinatario");
        if (!form.telefono.trim()) return toast.error("Ingresa el teléfono");
        if (!form.direccion.trim()) return toast.error("Ingresa la dirección");
        if (!form.departamento_id) return toast.error("Selecciona el departamento");
        if (!form.municipio_id) return toast.error("Selecciona el municipio / distrito");

        setSubmitting(true);
        try {
            const body = {
                nombre: form.nombre,
                telefono: form.telefono,
                correo: form.correo,
                direccion: form.direccion,
                reference: form.reference,
                departamento_id: Number(form.departamento_id),
                municipio_id: Number(form.municipio_id),
                tipo_servicio: form.tipo_servicio,
                tipo_entrega: form.tipo_entrega,
                peso: Number(form.peso) || 1,
                unidad_medida: form.unidad_medida,
                contenido: form.contenido,
                sv_plus_code: form.location?.plus_code || "",
                sv_plus_code_human: form.location?.plus_code_human || "",
                sv_maps_url: form.location?.maps_url || "",
                sv_lat: form.location?.lat || null,
                sv_lng: form.location?.lng || null,
            };
            if (form.tipo_servicio === "CCE") body.monto_cce = Number(form.monto_cce) || 10;

            const { data } = await api.post("/admin/c807-sample-label", body);
            if (!data.guia_numero) {
                toast.error("C807 no devolvió número de guía");
                return;
            }
            if (data.label_base64) {
                const byteChars = atob(data.label_base64);
                const bytes = new Uint8Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
                const blob = new Blob([bytes], { type: "application/pdf" });
                // Revoke any previous blob
                if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
                const url = URL.createObjectURL(blob);
                blobUrlRef.current = url;
                setResult({
                    guia_numero: data.guia_numero,
                    orden: data.orden,
                    mock: !!data.mock,
                    location_appended: !!data.location_appended,
                    blob_url: url,
                    filename: `etiqueta-c807-${data.guia_numero}.pdf`,
                });
                // First-attempt auto-download so the file is on disk immediately.
                const a = document.createElement("a");
                a.href = url;
                a.download = `etiqueta-c807-${data.guia_numero}.pdf`;
                a.rel = "noopener";
                a.style.display = "none";
                document.body.appendChild(a);
                a.click();
                setTimeout(() => { try { document.body.removeChild(a); } catch (_) { /* noop */ } }, 500);
                toast.success(`Guía ${data.guia_numero} generada · Descargando…`);
            } else if (data.pdf_error) {
                toast.warning(`Guía creada pero sin PDF: ${data.pdf_error.slice(0, 100)}`);
                setResult({ guia_numero: data.guia_numero, orden: data.orden, mock: !!data.mock, blob_url: null });
            } else {
                toast.success(`Guía de prueba ${data.guia_numero}${data.mock ? " (MOCK)" : ""}`);
                handleClose();
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error generando etiqueta");
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <div className="fixed inset-0 z-[1000] flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-6 px-3" data-testid="c807-sample-modal">
            <div className="bg-[var(--tb-bg)] border border-[var(--tb-gold)]/50 rounded-lg w-full max-w-3xl shadow-2xl my-4">
                <div className="sticky top-0 z-10 bg-[var(--tb-bg)] flex items-center justify-between p-4 border-b border-[var(--tb-border)]">
                    <div>
                        <h3 className="font-display text-xl text-[var(--tb-gold)] inline-flex items-center gap-2">
                            <Printer className="w-5 h-5" /> Etiqueta C807 de prueba
                        </h3>
                        <p className="text-[10px] text-[var(--tb-muted)] mt-0.5">Se crea una guía real en C807 con orden <code>TEST-*</code>. No genera cobros ni recolectas reales.</p>
                    </div>
                    <button onClick={handleClose} className="tb-btn-ghost !p-2" aria-label="Cerrar" data-testid="c807-sample-modal-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {/* Panel de resultado con acciones de descarga/impresion */}
                    {result && (
                        <div className="border-2 border-emerald-500/60 bg-emerald-500/10 rounded-lg p-4 space-y-3" data-testid="c807-sample-result">
                            <div className="flex items-start gap-2">
                                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm text-emerald-200">
                                        Guía generada: <span className="font-mono">{result.guia_numero}</span>
                                        {result.mock && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-500/30 border border-amber-500/50 text-[10px]">MOCK</span>}
                                    </div>
                                    <div className="text-[11px] text-[var(--tb-text-2)] mt-0.5">
                                        Orden interna: <code>{result.orden}</code>
                                        {result.location_appended && <span className="ml-2 text-emerald-300">· Tarjeta GPS + QR anexada</span>}
                                    </div>
                                    <div className="text-[10px] text-[var(--tb-muted)] mt-1">Guardada en el board de envíos C807 · siempre podrás reimprimirla desde ahí.</div>
                                </div>
                            </div>
                            {result.blob_url && (
                                <div className="flex flex-wrap gap-2">
                                    <a href={result.blob_url} download={result.filename}
                                        className="tb-btn-primary !text-xs inline-flex items-center gap-1.5"
                                        data-testid="c807-sample-download">
                                        <Download className="w-4 h-4" /> Descargar PDF
                                    </a>
                                    <button type="button"
                                        onClick={() => {
                                            const w = window.open(result.blob_url, "_blank");
                                            if (!w) { toast.error("Habilita ventanas emergentes para imprimir"); return; }
                                            // Wait for PDF to load and trigger print
                                            const tryPrint = () => { try { w.focus(); w.print(); } catch (_) { /* browser will handle */ } };
                                            setTimeout(tryPrint, 800);
                                        }}
                                        className="tb-btn-secondary !text-xs inline-flex items-center gap-1.5"
                                        data-testid="c807-sample-print">
                                        <Printer className="w-4 h-4" /> Imprimir ahora
                                    </button>
                                    <a href={result.blob_url} target="_blank" rel="noopener noreferrer"
                                        className="tb-btn-secondary !text-xs inline-flex items-center gap-1.5"
                                        data-testid="c807-sample-open">
                                        <ExternalLink className="w-4 h-4" /> Abrir en pestaña nueva
                                    </a>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Destinatario */}
                    <fieldset className="grid md:grid-cols-2 gap-3">
                        <label className="text-xs">Nombre destinatario *
                            <input className="tb-input mt-1" value={form.nombre}
                                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                                data-testid="c807-sample-nombre" />
                        </label>
                        <label className="text-xs">Teléfono *
                            <input className="tb-input mt-1" value={form.telefono}
                                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                                data-testid="c807-sample-telefono" />
                        </label>
                        <label className="text-xs md:col-span-2">Correo electrónico
                            <input className="tb-input mt-1" type="email" value={form.correo}
                                onChange={(e) => setForm({ ...form, correo: e.target.value })}
                                data-testid="c807-sample-correo" />
                        </label>
                    </fieldset>

                    {/* Dirección */}
                    <fieldset className="grid gap-3">
                        <label className="text-xs">Dirección (calle, colonia, número) *
                            <input className="tb-input mt-1" value={form.direccion}
                                onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                                data-testid="c807-sample-direccion" />
                        </label>
                        <label className="text-xs">Punto de referencia
                            <input className="tb-input mt-1" value={form.reference}
                                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                                data-testid="c807-sample-reference" />
                        </label>
                        <div className="grid md:grid-cols-2 gap-3">
                            <label className="text-xs">Departamento *
                                <select className="tb-input mt-1" value={form.departamento_id}
                                    onChange={(e) => setForm({ ...form, departamento_id: Number(e.target.value), municipio_id: "" })}
                                    data-testid="c807-sample-dep">
                                    {departamentos.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                                </select>
                            </label>
                            <label className="text-xs">Municipio / Distrito *
                                <select className="tb-input mt-1" value={form.municipio_id === "" ? "" : String(form.municipio_id)}
                                    onChange={(e) => setForm({ ...form, municipio_id: e.target.value === "" ? "" : Number(e.target.value) })}
                                    disabled={loadingMun || !municipios.length}
                                    data-testid="c807-sample-mun">
                                    <option value="">{loadingMun ? "Cargando..." : municipios.length ? "Selecciona..." : "Sin datos"}</option>
                                    {municipios.map((m) => <option key={m.id} value={String(m.id)}>{m.nombre}</option>)}
                                </select>
                            </label>
                        </div>
                    </fieldset>

                    {/* Ubicación mapa + Plus Code */}
                    <LocationPicker
                        value={form.location}
                        onChange={(loc) => setForm((f) => ({ ...f, location: loc }))}
                        departamentoName={depName}
                        municipioName={munName}
                        centerHint={centroidForDepartamento(form.departamento_id)}
                    />

                    {/* Servicio + paquete */}
                    <fieldset className="grid md:grid-cols-2 gap-3 border-t border-[var(--tb-border)] pt-4">
                        <label className="text-xs">Tipo de servicio *
                            <select className="tb-input mt-1" value={form.tipo_servicio}
                                onChange={(e) => setForm({ ...form, tipo_servicio: e.target.value })}
                                data-testid="c807-sample-tipo">
                                <option value="SER">SER — Servicio estándar</option>
                                <option value="CCE">CCE — Contra entrega (cobro al cliente)</option>
                            </select>
                        </label>
                        <label className="text-xs">Tipo de entrega
                            <select className="tb-input mt-1" value={form.tipo_entrega}
                                onChange={(e) => setForm({ ...form, tipo_entrega: e.target.value })}
                                data-testid="c807-sample-entrega">
                                <option value="NRML">NRML — Normal</option>
                                <option value="EXP">EXP — Express</option>
                            </select>
                        </label>
                        {form.tipo_servicio === "CCE" && (
                            <label className="text-xs md:col-span-2">Monto a cobrar (USD) *
                                <input className="tb-input mt-1" type="number" step="0.01" min="0"
                                    value={form.monto_cce}
                                    onChange={(e) => setForm({ ...form, monto_cce: e.target.value })}
                                    placeholder="10.00"
                                    data-testid="c807-sample-monto" />
                            </label>
                        )}
                        <label className="text-xs">Peso
                            <input className="tb-input mt-1" type="number" step="0.1" min="0.1"
                                value={form.peso}
                                onChange={(e) => setForm({ ...form, peso: e.target.value })}
                                data-testid="c807-sample-peso" />
                        </label>
                        <label className="text-xs">Unidad de medida
                            <select className="tb-input mt-1" value={form.unidad_medida}
                                onChange={(e) => setForm({ ...form, unidad_medida: e.target.value })}
                                data-testid="c807-sample-unidad">
                                <option value="LB">LB — Libras</option>
                                <option value="KG">KG — Kilogramos</option>
                            </select>
                        </label>
                        <label className="text-xs md:col-span-2">Contenido del paquete
                            <input className="tb-input mt-1" value={form.contenido}
                                onChange={(e) => setForm({ ...form, contenido: e.target.value })}
                                maxLength={120}
                                data-testid="c807-sample-contenido" />
                        </label>
                    </fieldset>
                </div>

                <div className="sticky bottom-0 z-10 bg-[var(--tb-bg)] flex items-center justify-end gap-2 p-4 border-t border-[var(--tb-border)]">
                    <button onClick={handleClose} className="tb-btn-secondary !text-xs" data-testid="c807-sample-cancel">
                        {result ? "Cerrar" : "Cancelar"}
                    </button>
                    <button
                        onClick={submit}
                        disabled={submitting}
                        className="tb-btn-primary inline-flex items-center gap-2 !text-xs disabled:opacity-50"
                        data-testid="c807-sample-submit">
                        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando…</> : <><Printer className="w-4 h-4" /> Generar etiqueta</>}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
